/**
 * Preview-link return relay (staging only).
 *
 * Companion to <dev-remark>. The badge copies a link shaped like
 *   https://…/products/x?preview_theme_id=Y#ep_return=%2Fproducts%2Fx%3F…
 * so a shared deep link survives the storefront password gate.
 *
 * Why this exists: on the password-gated staging store, a recipient without the
 * `storefront_digest` cookie who opens a deep link is 302'd to `/password`
 * (path + query dropped), and Shopify's password form always redirects to `/`
 * on success — so the intended page is lost. Shopify exposes no `return_to` for
 * the storefront password, and the form carries no destination field (verified
 * empirically against the staging gate).
 *
 * Two browser behaviors make a theme-side relay possible (both verified against
 * the live staging gate):
 *   1. A URL **#fragment survives the 302 to `/password`** (the redirect
 *      Location has no fragment, so the browser reapplies the original one).
 *   2. **`sessionStorage` survives the password POST → redirect to `/`** (same
 *      tab, same origin).
 *
 * So: on the gate we copy the fragment into sessionStorage (and do NOT redirect
 * — the visitor still has to enter the password); on the post-auth landing page
 * we read sessionStorage and `location.replace()` to the deep link, once.
 *
 * The password layout (`layout/password.liquid`) renders `content-for-footer`,
 * and `preview_theme_id` is honored on the gate even without the cookie, so this
 * module (bundled into entry-default) runs on the gate. The final hop runs on
 * whatever theme renders `/` after auth — the published staging theme — so full
 * end-to-end works once this is live on staging; the cookie-holder case (land
 * on the link directly) works immediately.
 *
 * Guarded on every axis: staging host only, no-op unless a marker is present,
 * same-origin relative paths only (no open redirect), and single-shot (the
 * marker is cleared before navigating) so it can't loop.
 *
 * Source path: theme/js/core/preview-return.esm.js
 * Built name : ./preview-return.js
 *
 * AI: Entirely written by AI
 * Date: 2026-09-14
 * Model: Claude Opus 4.8
 *
 * [PreviewReturn]
 */

import { log } from './log.js';

const KEY = 'ep_return';
const HASH_RE = /[#&]ep_return=([^&]+)/;
const GATE_PATH = '/password';

function isStaging() {
  // Staging serves from its custom domain; the myshopify.com handle still resolves too.
  return /3sixteen-dev-2|dev\.3sixteen\.com/.test(window.location.host);
}

// Same-origin path only: must start with a single '/' (rejects '//host',
// 'https://…', protocol-relative and scheme URLs). Restored via a relative
// location.replace, so the origin can never be redirected off-site.
function safePath(value) {
  return typeof value === 'string' && /^\/(?!\/)/.test(value) ? value : null;
}

function readStore() {
  try { return sessionStorage.getItem(KEY); } catch (e) { return null; }
}
function writeStore(value) {
  try { sessionStorage.setItem(KEY, value); } catch (e) { /* private mode */ }
}
function clearStore() {
  try { sessionStorage.removeItem(KEY); } catch (e) { /* private mode */ }
}

// Capture an `#ep_return=` marker from the URL into sessionStorage, then strip
// the fragment so it can't linger or re-trigger.
function captureFromHash() {
  const match = window.location.hash.match(HASH_RE);
  if (!match) return;
  let dest;
  try { dest = decodeURIComponent(match[1]); } catch (e) { return; }
  if (!safePath(dest)) return;
  writeStore(dest);
  log('[PreviewReturn] captured ' + dest);
  history.replaceState(null, '', window.location.pathname + window.location.search);
}

function restore() {
  const dest = safePath(readStore());
  if (!dest) { clearStore(); return; }
  // On the gate the visitor still has to enter the password — keep the marker
  // stashed and do not redirect.
  if (window.location.pathname === GATE_PATH) return;
  const here = window.location.pathname + window.location.search;
  if (here === dest) { clearStore(); return; } // already there
  clearStore(); // clear before navigating so we can never loop
  log('[PreviewReturn] restoring ' + dest);
  window.location.replace(dest);
}

if (isStaging()) {
  captureFromHash();
  restore();
}

log('[PreviewReturn] loaded');
